// The slide, painted small and instantly, while the real one loads underneath.
//
// Opening a WSI means DMV fitting the pyramid to the viewport and fetching the
// level that matches. Since ingest tops every pyramid up with a single-tile ~2K
// level (ingest/levels.py), that is now ONE request rather than hundreds — but
// it is still ~750 KB, and on the slow connections this exists for that is
// seconds during which the map shows empty tiles and reads as broken.
//
// So we cover it with the pregenerated thumbnail: ~26 KB, roughly thirty times
// faster to arrive, requested the moment this mounts — before DMV has finished
// initialising, let alone asked for a tile. Blurry for a beat, then the real
// level lands on top and this fades out.
//
// It is deliberately ONE small image. A 2K version used to load here too, until
// the pyramid gained a 2K level of its own carrying the same picture: that made
// the second request a duplicate download of the slide.
//
// Deliberately NOT view-synced: it sits at the fit-to-viewport position the map
// starts at, so it stops being correct the moment anyone pans or zooms. That's
// why `visible` drops on first interaction as well as on first paint.

import { useEffect, useRef, useState } from 'react';
import { getDataSource } from '../data/dataSourceFactory';

interface Props {
  studyInstanceUID: string;
  seriesInstanceUID: string;
  /** False retires the backdrop (fades out and stops rendering). */
  visible: boolean;
  /** Fires when the backdrop is actually covering the viewport, so the caller
   *  can drop its own loading indicator — a spinner over a painted slide reads
   *  as "still loading" when the whole point is that it looks done. */
  onCovering?: (covering: boolean) => void;
}

export function SlideBackdrop({
  studyInstanceUID,
  seriesInstanceUID,
  visible,
  onCovering,
}: Props) {
  const url = getDataSource().seriesThumbnailUrl(studyInstanceUID, seriesInstanceUID);

  const [loaded, setLoaded] = useState(false);
  // Keep the element mounted through the fade, then drop it so a stale slide
  // image can never reappear behind a later view.
  const [mounted, setMounted] = useState(true);

  useEffect(() => {
    if (visible) return;
    const t = setTimeout(() => setMounted(false), 400);
    return () => clearTimeout(t);
  }, [visible]);

  useEffect(() => {
    setLoaded(false);
    setMounted(true);
  }, [studyInstanceUID, seriesInstanceUID]);

  const shown = mounted && visible && loaded;

  // Held in a ref so a caller passing an inline callback doesn't re-fire this
  // on every render.
  const onCoveringRef = useRef(onCovering);
  onCoveringRef.current = onCovering;
  useEffect(() => {
    onCoveringRef.current?.(shown);
  }, [shown]);

  if (!mounted) return null;

  return (
    <div
      className="pointer-events-none absolute inset-0 z-[5]"
      style={{
        opacity: shown ? 1 : 0,
        // Asymmetric on purpose. Fading IN would ramp the image up from
        // transparent over the half-loaded tile grid — showing the exact mosaic
        // it exists to cover, through itself. It has to land opaque, in one
        // frame. Fading OUT is the opposite case: by then the tiles underneath
        // are the same picture, so a crossfade hides the swap.
        transition: shown ? 'none' : 'opacity 300ms',
      }}
      aria-hidden
    >
      {/* object-contain matches the map's initial fit-to-viewport framing. */}
      <img
        src={url}
        alt=""
        className="absolute inset-0 h-full w-full object-contain"
        onLoad={() => setLoaded(true)}
        // A series with no preview 404s; there's simply no backdrop.
        onError={() => setLoaded(false)}
      />
    </div>
  );
}
